import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Textarea, TextInput } from "flowbite-react";
import { Dialog, ToggleSwitch } from "@components";
import { inputSurface } from "@design-system";
import { IRecurrenceRule } from "../../types/order/IOrderTemplate";
import { RecurrenceFields } from "./RecurrenceFields";

export interface OrderTemplateSaveDialogProps {
  show: boolean;
  isLoading?: boolean;
  initialName?: string;
  initialDescription?: string;
  initialProjectNumber?: string;
  initialRecurrence?: IRecurrenceRule;
  onConfirm: (
    name: string,
    description: string,
    recurrence: IRecurrenceRule | null,
    projectNumber: string,
  ) => void;
  onCancel: () => void;
}

/** Local date as `YYYY-MM-DD` (avoids UTC shift from toISOString). */
const todayIso = (): string => {
  const d = new Date();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

/** Sensible starting rule when the user first enables recurrence. */
const defaultRecurrence = (): IRecurrenceRule => ({
  active: true,
  frequency: "weekly",
  interval: 1,
  // JS getDay(): 0 = Sun ... 6 = Sat -> convert to 0 = Mon ... 6 = Sun.
  byWeekday: [(new Date().getDay() + 6) % 7],
  startDate: todayIso(),
  endMode: "never",
  leadTimeDays: 3,
  timezone: "Europe/Oslo",
});

/**
 * Dialog used at the end of the wizard's "template mode" to name/describe the
 * template and optionally attach a recurrence schedule.
 *
 * Name/description stay uncontrolled (refs + defaultValue) to avoid the
 * historical focus-loss issue; recurrence is controlled state (unaffected by
 * re-renders and only shown when the toggle is on).
 */
export const OrderTemplateSaveDialog: FC<OrderTemplateSaveDialogProps> = (
  props,
) => {
  const { t } = useTranslation(["orders", "common"]);
  const nameRef = useRef<HTMLInputElement>(null);
  const projectNumberRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [recurrence, setRecurrence] = useState<IRecurrenceRule | null>(
    props.initialRecurrence ?? null,
  );

  // Sync recurrence state whenever the dialog (re)opens or its initial value
  // resolves (e.g. an existing template loading asynchronously in edit mode).
  useEffect(() => {
    if (props.show) {
      setRecurrence(props.initialRecurrence ?? null);
    }
  }, [props.show, props.initialRecurrence]);

  const handleToggleRecurrence = (enabled: boolean) => {
    setRecurrence(enabled ? (recurrence ?? defaultRecurrence()) : null);
  };

  const patchRecurrence = (patch: Partial<IRecurrenceRule>) =>
    setRecurrence((current) => (current ? { ...current, ...patch } : current));

  const handleConfirm = () => {
    const name = nameRef.current?.value.trim() ?? "";
    const description = descriptionRef.current?.value.trim() ?? "";
    if (!name) {
      nameRef.current?.focus();
      return;
    }
    props.onConfirm(
      name,
      description,
      recurrence,
      projectNumberRef.current?.value.trim() ?? "",
    );
  };

  const content = (
    <div className="space-y-4">
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="order-template-name"
            value={t("orders:templates.save_dialog.name_label")}
          />
        </div>
        <TextInput
          id="order-template-name"
          ref={nameRef}
          defaultValue={props.initialName ?? ""}
          required
          placeholder={t("orders:templates.save_dialog.name_ph")}
          theme={{ field: { input: { base: `w-full ${inputSurface}` } } }}
        />
      </div>
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="order-template-description"
            value={t("orders:templates.save_dialog.description_label")}
          />
        </div>
        <Textarea
          id="order-template-description"
          ref={descriptionRef}
          defaultValue={props.initialDescription ?? ""}
          rows={3}
          className={`${inputSurface} box-border w-full focus:ring-brand focus:border-brand`}
          placeholder={t("orders:templates.save_dialog.description_ph")}
        />
      </div>

      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="order-template-project-number"
            value={t("common:project_number")}
          />
        </div>
        <TextInput
          id="order-template-project-number"
          ref={projectNumberRef}
          defaultValue={props.initialProjectNumber ?? ""}
          theme={{ field: { input: { base: `w-full ${inputSurface}` } } }}
        />
      </div>

      {/* Recurrence */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={recurrence !== null}
            onChange={() => handleToggleRecurrence(recurrence === null)}
            ariaLabelledBy="order-template-recurrence-label"
          />
          <span
            id="order-template-recurrence-label"
            role="button"
            tabIndex={0}
            className="cursor-pointer select-none text-sm font-medium text-primary"
            onClick={() => handleToggleRecurrence(recurrence === null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleRecurrence(recurrence === null);
              }
            }}
          >
            {t("orders:templates.recurrence.enable")}
          </span>
        </div>
        {recurrence !== null && (
          <div className="mt-4">
            <RecurrenceFields value={recurrence} onChange={patchRecurrence} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      title={t("orders:templates.save_dialog.title")}
      show={props.show}
      size="lg"
      dismissible
      content={content}
      isLoading={props.isLoading}
      buttonConfirmText={t("common:save")}
      buttonCloseText={t("common:cancel")}
      confirmButton={handleConfirm}
      toggleModal={props.onCancel}
    />
  );
};

export default OrderTemplateSaveDialog;
